/**
 * Home v2 — design tokens.
 *
 * Ported from the `Home Plataforma v2.dc.html` prototype, then extended to
 * carry both themes. The prototype was dark-only; the app is not, so every
 * surface here declares a light value and a `dark:` override.
 *
 * The aurora is the one thing that genuinely changes character between themes
 * rather than just inverting: on dark it glows out of near-black, on light it
 * has to read as tinted daylight — brighter hues at lower density, or it turns
 * the page muddy.
 */

/** Account scenarios that drive the aurora, the alert pill and the task list. */
export const SCENARIOS = ["ok", "debito", "canal", "onboarding", "trial"];

/**
 * Dark aurora — saturated, high-alpha blobs glowing out of #0a0a0f.
 */
export const AURORA_DARK = {
  ok: ["rgba(96,110,255,.95)", "rgba(178,104,255,.8)", "rgba(52,196,232,.62)"],
  debito: ["rgba(255,48,64,.95)", "rgba(255,124,44,.82)", "rgba(206,26,104,.72)"],
  canal: ["rgba(255,164,32,.95)", "rgba(255,96,52,.78)", "rgba(236,196,52,.62)"],
  onboarding: ["rgba(64,214,158,.9)", "rgba(66,132,255,.78)", "rgba(152,112,255,.6)"],
  trial: ["rgba(122,104,255,.95)", "rgba(56,178,255,.8)", "rgba(206,104,255,.65)"],
};

/**
 * Light aurora — brighter, more chromatic hues at roughly half the alpha.
 *
 * Reusing the dark values on white produces grey sludge: high-alpha dark-tuned
 * colour over a light background reads as dirt, not light. These are pushed up
 * the chroma axis and pulled down in opacity so the effect stays airy.
 */
export const AURORA_LIGHT = {
  ok: ["rgba(109,120,255,.50)", "rgba(196,132,255,.44)", "rgba(56,214,244,.40)"],
  debito: ["rgba(255,92,108,.48)", "rgba(255,150,74,.42)", "rgba(240,86,158,.36)"],
  canal: ["rgba(255,186,66,.52)", "rgba(255,138,86,.42)", "rgba(250,214,84,.40)"],
  onboarding: ["rgba(64,222,168,.48)", "rgba(96,158,255,.42)", "rgba(178,146,255,.34)"],
  trial: ["rgba(150,132,255,.48)", "rgba(86,196,255,.44)", "rgba(224,138,255,.36)"],
};

export const AURORA = AURORA_DARK;

/** Radial stop used by every aurora blob. */
export const radial = (color) =>
  `radial-gradient(circle at 50% 50%, ${color} 0%, rgba(0,0,0,0) 66%)`;

/** Editorial card background — two tinted stops fading into the card surface. */
export const cardGradient = (from, to, tail = "rgba(255,255,255,.028)") =>
  `linear-gradient(140deg,${from},${to} 58%,${tail})`;

// ── Interactive primitives ──────────────────────────────────────────────────

/** Low-emphasis pill: suggestion chips, filter tabs, inline links. */
export const CHIP =
  "inline-flex items-center h-8 px-3.5 rounded-2xl border border-black/10 bg-black/[.04] dark:border-white/[.16] dark:bg-white/[.07] backdrop-blur-md cursor-pointer text-[12.5px] font-medium text-[#2c2d3a] dark:text-white/85 whitespace-nowrap flex-none";

/** Suggestion chip with a leading glyph — slightly taller and brighter. */
export const CHIP_ICON =
  "inline-flex items-center gap-2 h-[34px] px-[15px] rounded-[17px] border border-black/10 bg-white/70 dark:border-white/[.16] dark:bg-white/[.09] backdrop-blur-lg cursor-pointer text-[12.5px] font-semibold text-[#15161f] dark:text-white whitespace-nowrap flex-none transition-colors hover:bg-white dark:hover:bg-white/[.16]";

/** Outlined action — secondary next to a solid button. */
export const CHIP_TALL =
  "inline-flex items-center h-9 px-4 rounded-[18px] border border-black/15 dark:border-white/[.18] bg-transparent cursor-pointer text-[12.5px] font-semibold text-[#15161f] dark:text-white whitespace-nowrap flex-none transition-colors hover:bg-black/5 dark:hover:bg-white/10";

/** Primary action — maximum contrast against the page in either theme. */
export const BTN_SOLID =
  "inline-flex items-center h-9 px-[18px] rounded-[18px] border-0 bg-[#15161f] text-white dark:bg-white dark:text-[#0a0a0f] text-[12.5px] font-bold cursor-pointer whitespace-nowrap flex-none transition-opacity hover:opacity-90";

/** Destructive variant of the solid button, used by critical notices. */
export const BTN_SOLID_DANGER =
  "inline-flex items-center h-9 px-[18px] rounded-[18px] border-0 bg-[#e5443a] dark:bg-[#ff5a4d] text-white text-[12.5px] font-bold cursor-pointer whitespace-nowrap flex-none transition-opacity hover:opacity-90";

/** Shared shell for the editorial cards — the 3D tilt is applied imperatively. */
export const CARD_BASE =
  "flex items-stretch rounded-[22px] overflow-hidden [transform-style:preserve-3d] [will-change:transform] transition-[transform,box-shadow] duration-[350ms] ease-[cubic-bezier(.2,.7,.3,1)]";

/** Accent colours reused across kickers, task icons and notice tags. */
export const ACCENT = {
  danger: "#ff8b7f",
  warning: "#ffbe63",
  success: "#7ee2a8",
  info: "#b0b0ff",
  sky: "#84caff",
  pink: "#ff9ecd",
};

/** Same accents, darkened for adequate contrast on a light background. */
export const ACCENT_LIGHT = {
  danger: "#e0483a",
  warning: "#d98613",
  success: "#129c68",
  info: "#5b5bd6",
  sky: "#1d84d6",
  pink: "#d64f96",
};
